<div class="p-6">
  <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
    <div>
      <h3 class="font-bold">{{ '::ClientsReport' | abpLocalization }}</h3>
      <p class="text-sm text-gray-600 mt-1">{{ '::ClientsReportDescription' | abpLocalization }}</p>
    </div>
    <div class="flex items-center gap-2">
      <button 
        (click)="toggleViewMode()"
        [class.text-metro-blue]="isGrouped"
        [class.text-gray-600]="!isGrouped"
        class="p-2 rounded hover:bg-gray-100 transition-colors"
        [title]="isGrouped ? ('::Grouped' | abpLocalization) : ('::Ungrouped' | abpLocalization)">
        <span [innerHTML]="viewModeIcon" class="w-6 h-6 inline-block"></span>
      </button>
    </div>
  </div>

  <div class="flex flex-col md:flex-row gap-2 mt-3">
    <!-- Campo de búsqueda a la izquierda -->
    <div class="flex-1 w-full">
      <label class="text-sm font-medium text-metro-black mb-1 block">
        {{ '::Keyword' | abpLocalization }}
      </label>
      <div class="-my-2">
        <app-metro-search-input 
          (search)="onSearch($event)"
          (cleared)="onClearSearch()">
        </app-metro-search-input>
      </div>
    </div>
    
    <!-- Campos de fecha a la derecha -->
    <app-metro-date-input-picker 
      class="flex-1" 
      [modeInput]="'single'" 
      [label]="'::FromDate' | abpLocalization"
      [selectedDateInput]="fromDate"
      (dateSelectedChange)="onFromDateChange($event)" 
      autocomplete="off">
    </app-metro-date-input-picker>
    
    <app-metro-date-input-picker 
      class="flex-1" 
      [modeInput]="'single'" 
      [label]="'::ToDate' | abpLocalization"
      [selectedDateInput]="toDate"
      (dateSelectedChange)="onToDateChange($event)" 
      autocomplete="off">
    </app-metro-date-input-picker>
  </div>

  <div class="flex flex-col overflow-x-auto rounded-lg border-1 border-gray-700 mt-4">
    <table class="min-w-full divide-y divide-gray-200">
      <thead class="bg-gray-50">
        <tr>
          @for (col of columns; track col.name) {
            <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" [style.width]="col.width">
              {{ col.label | abpLocalization }}
            </th>
          }
            @if (isGrouped) {
              <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style="width: 5%;">
                {{ '::Details' | abpLocalization }}
              </th>
            }
        </tr>
      </thead>
      <tbody class="bg-white divide-y divide-gray-200">
        @if (isLoading) {
          @for (row of [1,2,3,4,5,6,7,8,9,10]; track row) {
            <tr class="animate-pulse">
              @for (col of columns; track col.name) {
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="h-4 bg-gray-200 rounded" [style.width]="row % 2 === 0 ? '75%' : '60%'"></div>
                </td>
              }
              @if (isGrouped) {
                <td class="px-6 py-4 whitespace-nowrap">
                  <div class="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
                </td>
              }
            </tr>
          }
        } @else {
          @for (client of data.items; track client.userId) {
            <tr class="hover:bg-gray-50">
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                {{ client.customerName }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                {{ client.customerEmail }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                {{ client.customerPhone }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                ${{ client.totalSpent.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              @if (isGrouped) {
                <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                  {{ client.totalOrders }}
                </td>
              }
              <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                @if (client.lastPurchaseDate) {
                  {{ client.lastPurchaseDate | date:'shortDate' }}
                } @else {
                  -
                }
              </td>
              @if (!isGrouped && client.orderDetails && client.orderDetails.length > 0) {
                <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                  {{ client.orderDetails[0].sommelierName || '-' }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-metro-blue-secondary">
                  {{ getOrderStatusTranslationKey(client.orderDetails[0].orderStatus) | abpLocalization }}
                </td>
              }
              @if (isGrouped) {
                <td class="px-6 py-4 whitespace-nowrap text-sm">
                  <button 
                    (click)="toggleRowDetails(client.userId)"
                    [disabled]="!client.userId"
                    class="text-metro-blue hover:text-metro-blue-secondary disabled:opacity-50">
                    @if (isRowExpanded(client.userId)) {
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7"></path>
                      </svg>
                    } @else {
                      <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                      </svg>
                    }
                  </button>
                </td>
              }
            </tr>
            @if (isGrouped && client.userId && isRowExpanded(client.userId)) {
              <tr>
                <td colspan="7" class="px-6 py-4 bg-gray-50">
                  <div class="ml-4">
                    <h4 class="font-semibold text-sm mb-2">{{ '::OrderDetails' | abpLocalization }}</h4>
                    @if (getOrderDetails(client.userId).length > 0) {
                      <table class="min-w-full divide-y divide-gray-200">
                        <thead class="bg-gray-100">
                          <tr>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-600">{{ '::OrderDate' | abpLocalization }}</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-600">{{ '::Total' | abpLocalization }}</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-600">{{ '::Status' | abpLocalization }}</th>
                            <th class="px-4 py-2 text-left text-xs font-medium text-gray-600">{{ '::Sommelier' | abpLocalization }}</th>
                          </tr>
                        </thead>
                        <tbody class="bg-white divide-y divide-gray-200">
                          @for (order of getOrderDetails(client.userId); track order.orderId) {
                            <tr>
                              <td class="px-4 py-2 text-sm">
                                @if (order.orderDate) {
                                  {{ order.orderDate | date:'shortDate' }}
                                } @else {
                                  -
                                }
                              </td>
                              <td class="px-4 py-2 text-sm">${{ order.total.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}</td>
                              <td class="px-4 py-2 text-sm">{{ getOrderStatusTranslationKey(order.orderStatus) | abpLocalization }}</td>
                              <td class="px-4 py-2 text-sm">{{ order.sommelierName || '-' }}</td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    } @else {
                      <p class="text-sm text-gray-500">{{ '::NoOrderDetails' | abpLocalization }}</p>
                    }
                  </div>
                </td>
              </tr>
            }
          }
        }
      </tbody>
    </table>

    @if (!isLoading) {
      <div class="mt-2">
        <app-metro-pagination
          [currentPage]="list.page"
          [totalItems]="data.totalCount"
          (pageChange)="onPageChange($event)"
          [pageSize]="10">
        </app-metro-pagination>
      </div>
    }
  </div>
</div>
